import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Camera } from "@phosphor-icons/react";

/**
 * HeroReel — a "live" gallery of real-character photos
 * that cycles in sync with the rotating headline word.
 * Cinemagraph feel: ken-burns + crossfade + scanlines + REC indicator.
 *
 * Frames are passed as [{src, label}] in order matching the rotating words.
 */
export default function HeroReel({ frames = [], interval = 3200 }) {
  const [i, setI] = useState(0);
  const [now, setNow] = useState(0);
  const wrapRef = useRef(null);

  // Cycle frames
  useEffect(() => {
    if (frames.length < 2) return;
    const t = setInterval(() => setI((v) => (v + 1) % frames.length), interval);
    return () => clearInterval(t);
  }, [frames.length, interval]);

  // 30-fps "recording time" counter
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (t) => {
      setNow((t - start) / 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Mouse parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18, mass: 0.4 });
  const sy = useSpring(my, { stiffness: 60, damping: 18, mass: 0.4 });
  const tX = useTransform(sx, [-1, 1], [-16, 16]);
  const tY = useTransform(sy, [-1, 1], [-16, 16]);
  const rX = useTransform(sy, [-1, 1], [5, -5]);
  const rY = useTransform(sx, [-1, 1], [-5, 5]);
  const glowX = useTransform(sx, [-1, 1], [-30, 30]);
  const glowY = useTransform(sy, [-1, 1], [-30, 30]);

  const onMouseMove = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };
  const onMouseLeave = () => { mx.set(0); my.set(0); };

  const active = frames[i] || frames[0] || {};
  const mins = Math.floor(now / 60).toString().padStart(2, "0");
  const secs = Math.floor(now % 60).toString().padStart(2, "0");
  const ms = Math.floor((now % 1) * 100).toString().padStart(2, "0");

  return (
    <div
      ref={wrapRef}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className="relative w-full max-w-[480px] mx-auto aspect-[3/4]"
      data-testid="hero-reel"
    >
      {/* Glow halo */}
      <motion.div aria-hidden style={{ x: glowX, y: glowY }} className="absolute inset-0 -z-10">
        <motion.div
          animate={{ scale: [1, 1.08, 1], opacity: [0.55, 0.85, 0.55] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -inset-12 rounded-full bg-gradient-to-tr from-[#FF6B9E] via-[#A78BFA] to-[#38BDF8] blur-3xl"
        />
      </motion.div>

      {/* Frame with 3D tilt */}
      <motion.div
        style={{
          rotateX: rX,
          rotateY: rY,
          x: tX,
          y: tY,
          transformPerspective: 1000,
          transformStyle: "preserve-3d",
        }}
        className="relative w-full h-full rounded-3xl overflow-hidden ring-1 ring-black/10 shadow-[0_40px_80px_-30px_rgba(15,23,42,0.45)] bg-slate-200"
      >
        {/* Image stack (crossfade) */}
        <AnimatePresence>
          <motion.img
            key={i}
            src={active.src}
            alt={active.label || "Hangang Global"}
            draggable={false}
            initial={{ opacity: 0, scale: 1.12 }}
            animate={{ opacity: 1, scale: 1.05 }}
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{
              opacity: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
              scale: { duration: interval / 1000 + 1.2, ease: "linear" },
            }}
            onError={(e) => { e.currentTarget.style.opacity = "0"; }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>

        {/* Vignette */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse at center, rgba(0,0,0,0) 50%, rgba(0,0,0,0.40) 100%)"
        }} />
        {/* Color cast */}
        <div className="absolute inset-0 mix-blend-soft-light bg-gradient-to-tr from-[#FF6B9E]/30 via-transparent to-[#38BDF8]/25" />
        {/* Subtle scanlines */}
        <div className="absolute inset-0 pointer-events-none opacity-25 mix-blend-overlay"
             style={{ backgroundImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.05) 0 2px, transparent 2px 4px)" }} />

        {/* TOP overlay row */}
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-white">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur text-[10px] tracking-[0.28em] uppercase">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
            </span>
            REC · {mins}:{secs}:{ms}
          </div>
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur text-[10px] tracking-[0.28em] uppercase">
            <Camera size={11} weight="bold" /> 50mm · ƒ1.8
          </div>
        </div>

        {/* BOTTOM caption */}
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45 }}
              className="flex items-end justify-between gap-3"
            >
              <div className="leading-tight">
                <div className="text-[10px] tracking-[0.28em] uppercase opacity-80">{active.kicker || "Frame"}</div>
                <div className="font-display text-lg font-light tracking-tight">{active.label}</div>
              </div>
              <div className="font-display text-[10px] tracking-[0.28em] uppercase opacity-80">
                {String(i + 1).padStart(2, "0")} / {String(frames.length).padStart(2, "0")}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Frame ticks (right edge) */}
        <div className="absolute top-1/2 -translate-y-1/2 right-2.5 flex flex-col gap-1.5 items-center">
          {frames.map((_, idx) => (
            <span
              key={idx}
              className={`block w-[3px] rounded-full transition-all duration-500 ${
                idx === i ? "h-7 bg-white" : "h-2 bg-white/40"
              }`}
            />
          ))}
        </div>
      </motion.div>

      {/* Rotating circular text below */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
        className="absolute -bottom-7 -left-7 w-28 h-28 pointer-events-none"
        aria-hidden
      >
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <defs>
            <path id="circleTextReel" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
          </defs>
          <text fontFamily="Unbounded, sans-serif" fontSize="8" fontWeight="500" letterSpacing="4" fill="#0F172A">
            <textPath href="#circleTextReel">SEOUL · STUDIO · 2026 · HANGANG · </textPath>
          </text>
        </svg>
      </motion.div>
    </div>
  );
}
